/**
 * Free socket tester — one per order with a bedsheet (any of its three
 * products), mat, fitted sheet or bundle (QUALIFYING_HANDLES). Shoes and
 * accessories don't qualify.
 *
 * The gift is the ordinary paid Socket Tester, added with the hidden
 * `_tester_gift` line property. It is made $0 by an automatic Buy X Get Y
 * discount in Shopify admin (buy any qualifying product, get the Socket Tester
 * 100% off, once per order), not here. cart-drawer-item.liquid and
 * cart-line-item.liquid read the property to show "FREE" and to drop the
 * stepper, remove link and product link. A tester the shopper adds themselves
 * has no property and is left alone.
 *
 * Rules, kept deliberately simple (shoppers stay on one landing page and pick
 * one plug):
 *   - The first qualifying product added sets the tester's plug type. It is
 *     never swapped after that, whatever else is added.
 *   - Exactly one gift line, at quantity 1.
 *   - No qualifying product left in the cart: the gift goes.
 *
 * Theme settings > Free socket tester (free_tester_enabled, passed in from
 * theme.liquid) switches it off: then no gift is added, and any gift already
 * in a cart is taken out on the shopper's next page load or cart change.
 */
(function () {
  'use strict';

  if (window.__gwTesterGiftLoaded) return;
  window.__gwTesterGiftLoaded = true;

  var TESTER_HANDLE = 'earth-ground-socket-tester';
  // The bedsheet is three products: the main one, a copy on the same template
  // (grounding-bedsheet) and the Spanish one. All three qualify. Keep this list
  // in step with "Customer buys" in the Socket Tester Buy X Get Y discount.
  var QUALIFYING_HANDLES = [
    'groundingwell-bedsheet',
    'grounding-bedsheet',
    'groundingwell-bedsheet-es',
    'groundingwell-mat',
    'groundingwell-fitted-sheet',
    'groundingwell-bundle'
  ];
  var GIFT_PROPERTY = '_tester_gift';
  var ENABLED = window.gwTesterGiftEnabled !== false;
  var PLUG_OPTION = 'plug type';
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Taken before this file wraps fetch, so the script's own cart calls are not
  // seen as shopper changes. They still pass through the quantity-consolidation
  // wrapper in custom-js.liquid, which lets an `items: [...]` add through
  // untouched because it has no top-level `id`.
  var baseFetch = window.fetch.bind(window);

  var testerVariants = null;
  // Whether the cart has the gift: true, false, or null when unknown. Lets the
  // fast path below act on an add-to-cart without reading the cart first.
  var hasGift = document.cookie.indexOf('cart=') === -1 ? false : null;
  var fastAdd = null; // the fast path's add while it is in flight
  var syncing = false;
  var pending = false;
  var timer = null;
  var watching = 0; // shopper cart changes not yet handed to onCartMutation
  var giftAdding = 0; // gift adds from sync() not yet landed

  function plugOf(line) {
    var options = line.options_with_values || [];
    for (var i = 0; i < options.length; i++) {
      if ((options[i].name || '').trim().toLowerCase() === PLUG_OPTION) return options[i].value;
    }
    return null;
  }

  // The products say "AU", the tester says "AU/NZ"; compare the part before "/".
  function samePlug(a, b) {
    var key = function (value) { return String(value).split('/')[0].trim().toUpperCase(); };
    return key(a) === key(b);
  }

  function qualifies(line) {
    return QUALIFYING_HANDLES.indexOf(line.handle) !== -1;
  }

  function isGift(line) {
    return line.handle === TESTER_HANDLE && !!(line.properties && line.properties[GIFT_PROPERTY]);
  }

  function cartJSON() {
    return baseFetch(root + 'cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  function post(path, body) {
    return baseFetch(root + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) throw new Error(path + ' ' + r.status);
      return r.json();
    });
  }

  function loadTester() {
    if (testerVariants) return Promise.resolve(testerVariants);
    return baseFetch(root + 'products/' + TESTER_HANDLE + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('tester product ' + r.status);
        return r.json();
      })
      .then(function (product) {
        testerVariants = product.variants;
        return testerVariants;
      });
  }

  // The tester's variants are its plug types, matched on value so the option
  // can be named anything.
  function testerVariantFor(plug) {
    if (!plug || !testerVariants) return null;
    for (var i = 0; i < testerVariants.length; i++) {
      var options = testerVariants[i].options || [];
      for (var j = 0; j < options.length; j++) {
        if (samePlug(options[j], plug)) return testerVariants[i];
      }
    }
    return null;
  }

  function giftLine(variant) {
    var properties = {};
    properties[GIFT_PROPERTY] = 'true';
    return { id: variant.id, quantity: 1, properties: properties };
  }

  // Paints the drawer from the sections the request returned, rather than the
  // drawer's own renderContents (which also reopens it) or onCartDrawerUpdate
  // (one more round trip).
  function refreshCartUI(response) {
    if (/\/cart\/?$/.test(location.pathname)) {
      location.reload();
      return;
    }
    var drawer = document.querySelector('m-cart-drawer');
    if (!drawer) return;
    var html = response && response.sections && response.sections['cart-drawer'];
    if (!html || typeof drawer.getSectionsToRender !== 'function') {
      if (typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate();
      return;
    }
    drawer.getSectionsToRender().forEach(function (section) {
      var target = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (target) target.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
    });
    if (typeof drawer.updateCartCount === 'function') {
      if (typeof response.item_count === 'number') drawer.updateCartCount(response.item_count);
      else if (typeof drawer.getCart === 'function') drawer.getCart().then(function (cart) { drawer.updateCartCount(cart.item_count); });
    }
    if (window.FoxKitV2 && window.FoxKitV2.Modules.InCart) window.FoxKitV2.Modules.InCart.getCart();
  }

  // Fast path: the add-to-cart response says what went in, so when the cart is
  // known to have no gift yet and a qualifying product was just added, the
  // tester for its plug goes straight in (with the redrawn drawer in the same
  // response) instead of waiting on a read of the cart, which queues behind the
  // theme's own cart requests. The full sync that follows tidies anything else.
  function addGiftFor(data) {
    if (!ENABLED || hasGift !== false || !testerVariants) return false;
    var items = (data && data.items) || (data && data.variant_id ? [data] : []);
    var first = items.filter(qualifies)[0];
    var variant = first && testerVariantFor(plugOf(first));
    if (!variant) return false;
    hasGift = true;
    fastAdd = post('cart/add.js', { items: [giftLine(variant)], sections: 'cart-drawer', sections_url: location.pathname })
      .then(refreshCartUI)
      .catch(function (error) {
        hasGift = null;
        console.error('[TesterGift]', error);
      })
      .then(function () { fastAdd = null; });
    return true;
  }

  function sync() {
    if (syncing) {
      pending = true;
      return;
    }
    syncing = true;

    // Read the cart only after a fast-path add has landed.
    (fastAdd || Promise.resolve())
      .then(cartJSON)
      .then(function (cart) {
        // Switched off: nothing qualifies, so any gift left in the cart is removed.
        var qualifying = ENABLED ? cart.items.filter(qualifies) : [];
        var gifts = cart.items.filter(isGift);
        if (!qualifying.length && !gifts.length) {
          hasGift = false;
          return null;
        }
        return loadTester().then(function () {
          var updates = {};
          var removals = false;
          var add = null;

          if (!qualifying.length) {
            // Nothing qualifies any more: the gift goes.
            gifts.forEach(function (line) { updates[line.key] = 0; });
            removals = true;
          } else if (!gifts.length) {
            // The cart lists the newest line first, so the last qualifying line
            // is the first one added, and its plug is the tester's.
            var variant = testerVariantFor(plugOf(qualifying[qualifying.length - 1]));
            if (variant) add = giftLine(variant);
          } else {
            // Keep the first gift added, at quantity 1; drop any others.
            var keep = gifts[gifts.length - 1];
            gifts.forEach(function (line) {
              if (line !== keep) {
                updates[line.key] = 0;
                removals = true;
              }
            });
            if (keep.quantity !== 1) {
              updates[keep.key] = 1;
              removals = true;
            }
          }

          hasGift = qualifying.length > 0 && (gifts.length > 0 || !!add);
          var sections = { sections: 'cart-drawer', sections_url: location.pathname };
          var chain = Promise.resolve();
          if (removals) {
            chain = chain.then(function () {
              return post('cart/update.js', Object.assign({ updates: updates }, sections)).then(refreshCartUI);
            });
          }
          if (add) {
            // Counted from now, not from when the request goes out, so
            // gwTesterGiftSettled never sees hasGift = true before the gift is in.
            giftAdding++;
            chain = chain.then(function () {
              return post('cart/add.js', Object.assign({ items: [add] }, sections)).then(refreshCartUI);
            }).then(function () {
              giftAdding--;
            }, function (error) {
              giftAdding--;
              throw error;
            });
          }
          return chain;
        });
      })
      .catch(function (error) {
        hasGift = null;
        console.error('[TesterGift]', error);
      })
      .then(function () {
        syncing = false;
        if (pending) {
          pending = false;
          schedule(0);
        }
      });
  }

  function schedule(delay) {
    clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      sync();
    }, delay);
  }

  // An add-to-cart syncs at once (or, after the fast path, shortly after). Other
  // changes wait briefly, long enough for the drawer's remove-then-add
  // plug/quantity swaps to land as one change.
  function onCartMutation(url, fast) {
    schedule(/\/cart\/add/.test(url) && !fast ? 0 : 400);
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var result = baseFetch.apply(window, arguments);
    // The script's own calls use baseFetch directly, so anything seen here is
    // the shopper (or another app); a change that lands mid-sync re-queues it.
    if (CART_MUTATION.test(url)) {
      watching++;
      var seen = function (fast) {
        watching--;
        onCartMutation(url, fast);
      };
      result.then(function (response) {
        if (/\/cart\/add/.test(url) && response && response.ok) {
          response.clone().json()
            .then(function (data) { seen(addGiftFor(data)); })
            .catch(function () { seen(false); });
        } else {
          seen(false);
        }
      }, function () { seen(false); });
    }
    return result;
  };

  // Resolves once the gift is settled for the shopper's latest change: no cart
  // change waiting to be looked at, no gift add in flight, and (unless the gift
  // is already in) no sync running or queued. gw-direct-checkout.js
  // waits on this before leaving for checkout, because leaving the page cancels
  // a gift add still in flight. The tidy-up sync after a fast-path add is not
  // waited for: the gift is already in. Switched off, there is no gift to wait
  // for.
  window.gwTesterGiftSettled = function () {
    if (!ENABLED) return Promise.resolve();
    return new Promise(function (resolve) {
      (function check() {
        if (!watching && !fastAdd && !giftAdding && (hasGift === true || (!syncing && !timer && !pending))) resolve();
        else setTimeout(check, 50);
      })();
    });
  };

  var origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (CART_MUTATION.test(String(url))) {
      this.addEventListener('loadend', function () { onCartMutation(String(url), false); });
    }
    return origOpen.apply(this, arguments);
  };

  // Fetch the tester's variants up front so the fast path has them on any page
  // a qualifying product can be added from (PDPs, ?view= ad templates, LPs). A
  // cart is checked once the page has loaded (that also covers a full-page add).
  function onLoad() {
    if (ENABLED) loadTester().catch(function () {});
    if (document.cookie.indexOf('cart=') !== -1) schedule(0);
  }
  if (document.readyState === 'complete') onLoad();
  else window.addEventListener('load', onLoad);
})();
